/**
 * Data transformations for stock report
 * @see US-16.5 - UI Rapport Principal
 */

import type { StockReportRow, CategoryPathItem } from '@bstock/shared';

/**
 * Flat row structure for DataGrid display
 * categoryPath is expanded into categoryLevel0, categoryLevel1, etc.
 */
export interface FlatStockReportRow extends Omit<StockReportRow, 'categoryPath'> {
  // Category levels are added dynamically
  [key: `categoryLevel${number}`]: string | undefined;
}

/**
 * Transform API rows to flat structure for DataGrid
 * Converts categoryPath array to flat categoryLevelN columns
 */
export function transformForDataGrid(rows: StockReportRow[]): {
  data: FlatStockReportRow[];
  maxDepth: number;
} {
  // Find max category depth
  const maxDepth = Math.max(...rows.map((r) => r.categoryPath?.length ?? 0), 0);

  // Transform each row
  const data: FlatStockReportRow[] = rows.map((row) => {
    const { categoryPath, ...rest } = row;
    const flat: FlatStockReportRow = { ...rest };

    // Expand categoryPath into categoryLevelN columns
    for (let i = 0; i < maxDepth; i++) {
      flat[`categoryLevel${i}`] = categoryPath[i]?.name;
    }

    return flat;
  });

  return { data, maxDepth };
}

/**
 * Category level labels
 */
const CATEGORY_LEVEL_LABELS = [
  'Cat. Principale',
  'Cat. Secondaire',
  'Cat. Tertiaire',
  'Cat. Niveau 4',
  'Cat. Niveau 5',
  'Cat. Niveau 6',
];

/**
 * Get label for a category level
 */
export function getCategoryLevelLabel(level: number): string {
  return CATEGORY_LEVEL_LABELS[level] ?? `Cat. Niveau ${level + 1}`;
}

/**
 * Generate column definitions for category levels
 */
export function generateCategoryColumns(maxDepth: number) {
  return Array.from({ length: maxDepth }, (_, i) => ({
    dataField: `categoryLevel${i}`,
    caption: getCategoryLevelLabel(i),
    groupIndex: i, // For grouping in DataGrid
    width: 150,
  }));
}

/**
 * Format variance with color indication
 * Positive variance (loss) = red
 * Negative variance (gain) = green
 */
export function getVarianceClassName(value: number): string {
  if (value > 0) return 'text-danger-600';
  if (value < 0) return 'text-success-600';
  return '';
}

/**
 * Format variance with sign prefix
 */
export function formatVariance(value: number, decimals: number = 2): string {
  const formatted = Math.abs(value).toFixed(decimals);
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `-${formatted}`;
  return formatted;
}

/**
 * Build breadcrumb string from category path
 */
export function buildCategoryBreadcrumb(categoryPath: CategoryPathItem[]): string {
  return categoryPath.map((c) => c.name).join(' > ');
}
